import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useUserContext } from "../../context/UserContext";

export default function Payment({ payAmount, dueAmount, invoiceId, table_id }) {
  const { user } = useUserContext();
  const API = process.env.REACT_APP_BASE_URL;

  const [activeTab, setActiveTab] = useState("Cash");
  const [members, setMembers] = useState([]);
  const [formData, setFormData] = useState({
    cash_amount: "",
    card_amount: "",
    epay_amount: "",
    epay_type: "",
    card_number: "",
    transaction_id: "",
    cheque_number: "",
    customer_name: "",
    remarks: "",
    approved_by: "",
  });

  const toNumber = (v) => parseFloat(v) || 0;

  // Fetch members
  useEffect(() => {
    axios
      .get(`${API}/user/get_members`)
      .then((res) => setMembers(res.data.items || []))
      .catch(console.error);
  }, []);

  // Initialize fields on tab or dueAmount change
  useEffect(() => {
    const d = toNumber(dueAmount);
    switch (activeTab) {
      case "Cash":
        setFormData((f) => ({ ...f, cash_amount: d.toFixed(2) }));
        break;
      case "Card":
        setFormData((f) => ({ ...f, card_amount: d.toFixed(2) }));
        break;
      case "E-Payment":
        setFormData((f) => ({ ...f, epay_amount: d.toFixed(2) }));
        break;
      case "Cash & Card":
        setFormData((f) => ({
          ...f,
          cash_amount: (d / 2).toFixed(2),
          card_amount: (d / 2).toFixed(2),
        }));
        break;
      case "Cash & E-Payment":
        setFormData((f) => ({
          ...f,
          cash_amount: (d / 2).toFixed(2),
          epay_amount: (d / 2).toFixed(2),
        }));
        break;
      default:
        // reset others
        setFormData((f) => ({
          ...f,
          cash_amount: "",
          card_amount: "",
          epay_amount: "",
        }));
    }
  }, [activeTab, dueAmount]);

  // Keep cash = due - other in split cases
  useEffect(() => {
    const d = toNumber(dueAmount);
    if (activeTab === "Cash & Card") {
      const c = toNumber(formData.card_amount);
      setFormData((f) => ({ ...f, cash_amount: (d - c).toFixed(2) }));
    }
    if (activeTab === "Cash & E-Payment") {
      const e = toNumber(formData.epay_amount);
      setFormData((f) => ({ ...f, cash_amount: (d - e).toFixed(2) }));
    }
  }, [formData.card_amount, formData.epay_amount, activeTab, dueAmount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      ["cash_amount", "card_amount", "epay_amount"].includes(name) &&
      !/^\d*\.?\d*$/.test(value)
    )
      return;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleConfirmPayment = async () => {
    const base = {
      invoice_id: invoiceId,
      table_id,
      customer_name: formData.customer_name,
      remarks: formData.remarks,
      approved_by: formData.approved_by,
      created_by: user,
    };

    try {
      // Compound: Cash & Card
      if (activeTab === "Cash & Card") {
        await axios.post(`${API}/orders/processPayment`, {
          ...base,
          payment_method: "Cash_&_Card",
          cash_amount: toNumber(formData.cash_amount),
          card_amount: toNumber(formData.card_amount),
          card_number: formData.card_number,
        });
      }
      // Compound: Cash & E-Payment
      else if (activeTab === "Cash & E-Payment") {
        await axios.post(`${API}/orders/processPayment`, {
          ...base,
          payment_method: "Cash_&_E-Payment",
          cash_amount: toNumber(formData.cash_amount),
          epay_amount: toNumber(formData.epay_amount),
          epay_type: formData.epay_type,
          transaction_id: formData.transaction_id,
        });
      }
      // Single‐method fallback
      else {
        const amountPaid =
          toNumber(formData.cash_amount) ||
          toNumber(formData.card_amount) ||
          toNumber(formData.epay_amount) ||
          toNumber(dueAmount);

        await axios.post(`${API}/orders/processPayment`, {
          ...base,
          payment_method: activeTab,
          amount_paid: amountPaid,
          card_number: formData.card_number,
          epay_type: formData.epay_type,
          transaction_id: formData.transaction_id,
          cheque_number: formData.cheque_number,
        });
      }

      toast.success("Payment processed successfully!");
      setTimeout(() => (window.location.href = "/take_order"), 800);
    } catch (err) {
      console.error(err);
      toast.error("Failed to process payment.");
    }
  };

  return (
    <>
      <div className="BillHeader">
        <h2>Bill Amount:</h2>
        <input readOnly value={payAmount} />
        <h2>Due Amount:</h2>
        <input readOnly value={dueAmount} />
      </div>

      <div className="BillContainer">
        <div className="PaymentType">
          <ul>
            {[
              "Cash",
              "Card",
              "Cash & Card",
              "E-Payment",
              "Cash & E-Payment",
              "Cheque",
              "Credit",
              "Complementary",
            ].map((m) => (
              <li
                key={m}
                className={activeTab === m ? "active" : ""}
                onClick={() => setActiveTab(m)}
              >
                {m}
              </li>
            ))}
          </ul>
        </div>
        <div className="PaymentField">
          {activeTab.includes("Cash") && (
            <div className="formgroup">
              <label>Cash Amount</label>
              <input
                name="cash_amount"
                value={formData.cash_amount}
                onChange={handleChange}
              />
            </div>
          )}
          {activeTab.includes("Card") && (
            <>
              <div className="formgroup">
                <label>Card Amount</label>
                <input
                  name="card_amount"
                  value={formData.card_amount}
                  onChange={handleChange}
                />
              </div>
              <div className="formgroup">
                <label>Card Number</label>
                <input
                  name="card_number"
                  value={formData.card_number}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          {activeTab.includes("E-Payment") && (
            <>
              <div className="formgroup">
                <label>Epay Amount</label>
                <input
                  name="epay_amount"
                  value={formData.epay_amount}
                  onChange={handleChange}
                />
              </div>
              <div className="formgroup">
                <label>Epay Type</label>
                <select
                  name="epay_type"
                  value={formData.epay_type}
                  onChange={handleChange}
                >
                  <option />
                  <option>IMEPay</option>
                  <option>Esewa</option>
                  <option>Khalti</option>
                  <option>PhonePay</option>
                  <option>Others</option>
                </select>
              </div>
              <div className="formgroup">
                <label>Transaction ID</label>
                <input
                  name="transaction_id"
                  value={formData.transaction_id}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          {activeTab === "Cheque" && (
            <>
              <div className="formgroup">
                <label>Cheque Number</label>
                <input
                  name="cheque_number"
                  value={formData.cheque_number}
                  onChange={handleChange}
                />
              </div>
              <div className="formgroup">
                <label>Remarks</label>
                <input
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          {(activeTab === "Credit" || activeTab === "Complementary") && (
            <>
              <div className="formgroup">
                <label>Select Member</label>
                <select
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                >
                  <option />
                  {members.map((m) => (
                    <option key={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div className="formgroup">
                <label>Remarks</label>
                <input
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>
              {activeTab === "Complementary" && (
                <div className="formgroup">
                  <label>Approved By (Optional)</label>
                  <input
                    name="approved_by"
                    value={formData.approved_by}
                    onChange={handleChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="PayActions">
        <button onClick={() => window.location.reload()}>Cancel</button>
        <button onClick={handleConfirmPayment}>Confirm Payment</button>
      </div>
    </>
  );
}
